import { useCallback, useEffect, useState } from "react";
import { Inbox, CheckCircle2, Eye, RotateCcw, Link2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Moderator feedback inbox: read, filter by type/status, resolve. */

const TYPE_STYLES = {
  comment: "bg-sky-100 text-sky-800 border-sky-300",
  complaint: "bg-rose-100 text-rose-800 border-rose-300",
  suggestion: "bg-violet-100 text-violet-800 border-violet-300",
  appreciation: "bg-emerald-100 text-emerald-800 border-emerald-300",
  other: "bg-zinc-100 text-zinc-700 border-zinc-300",
};
const STATUSES = ["new", "reviewed", "resolved"];
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  const load = useCallback(() => api.get("/feedback").then((r) => setItems(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setSt = async (id, st) => {
    try { await api.put(`/feedback/${id}/status`, { status: st }); load(); }
    catch { toast.error("Failed to update"); }
  };

  const filtered = items.filter((i) => (!status || i.status === status) && (!type || i.type === type));
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: items.filter((i) => i.status === s).length }), {});

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-feedback-page">
      <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3 mb-2">
        <Inbox className="w-8 h-8 text-emerald-600" /> Feedback Inbox
      </h1>
      <p className="text-muted-foreground mb-6">Everything travelers send through the feedback form — with the source page so fixes are easy to locate.</p>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setStatus("")} data-testid="filter-all"
          className={`px-3 py-1.5 text-sm border ${!status ? "border-emerald-600 bg-emerald-50 font-700" : "border-zinc-300"}`}>All ({items.length})</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-${s}`}
            className={`px-3 py-1.5 text-sm border capitalize ${status === s ? "border-emerald-600 bg-emerald-50 font-700" : "border-zinc-300"}`}>
            {s} ({counts[s] || 0})
          </button>
        ))}
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="filter-type"
          className="ml-auto border border-zinc-300 px-2 py-1.5 text-sm bg-white">
          <option value="">All types</option>
          {Object.keys(TYPE_STYLES).map((t) => <option key={t} value={t} className="capitalize">{t[0].toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {filtered.map((f) => (
          <div key={f.id} className={`border bg-white p-4 ${f.status === "new" ? "border-emerald-400" : "border-zinc-300"}`} data-testid={`feedback-item-${f.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`text-xs font-700 border px-2 py-0.5 capitalize ${TYPE_STYLES[f.type] || TYPE_STYLES.other}`}>
                {f.type === "other" && f.other_type ? `Other: ${f.other_type}` : f.type}
              </span>
              <span className="font-700">{f.name}</span>
              <span className="text-muted-foreground text-xs">{fmt(f.created_at)}</span>
              <span className={`ml-auto text-xs font-700 px-2 py-0.5 capitalize ${f.status === "new" ? "bg-emerald-100 text-emerald-800" : f.status === "resolved" ? "bg-zinc-100 text-zinc-500" : "bg-amber-100 text-amber-800"}`}>{f.status}</span>
            </div>
            <p className="text-sm text-zinc-800 mt-2 whitespace-pre-line">{f.message}</p>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {f.email}</span>
              <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {f.phone}</span>
              {f.page_url && <span className="inline-flex items-center gap-1 text-indigo-700 font-600" data-testid={`feedback-source-${f.id}`}><Link2 className="w-3.5 h-3.5" /> {f.page_url}</span>}
              <span className="ml-auto flex gap-2">
                {f.status !== "reviewed" && f.status !== "resolved" && (
                  <button onClick={() => setSt(f.id, "reviewed")} data-testid={`mark-reviewed-${f.id}`} className="inline-flex items-center gap-1 text-amber-700 font-700 hover:underline"><Eye className="w-3.5 h-3.5" /> Mark reviewed</button>
                )}
                {f.status !== "resolved" && (
                  <button onClick={() => setSt(f.id, "resolved")} data-testid={`mark-resolved-${f.id}`} className="inline-flex items-center gap-1 text-emerald-700 font-700 hover:underline"><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>
                )}
                {f.status === "resolved" && (
                  <button onClick={() => setSt(f.id, "new")} data-testid={`reopen-${f.id}`} className="inline-flex items-center gap-1 text-zinc-500 font-700 hover:underline"><RotateCcw className="w-3.5 h-3.5" /> Reopen</button>
                )}
              </span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-muted-foreground text-sm py-10 text-center">No feedback matches this filter.</p>}
      </div>
    </div>
  );
}
